import { IAgencyDigest } from './agencyDigest';
import { IOutcomeClient } from './agencyOutcomes';

/**
 * How much of the book has a family actually contributing, over the last seven days.
 *
 * The honest constraint shapes this. Innerhive cannot claim it kept a client home longer or kept a
 * caregiver in post: nobody has measured the counterfactual, and a tool that invents one is worse
 * than a tool that stays quiet. What it CAN show is who is on the record and who is not, which is a
 * fact about the record and the thing an agency works next.
 *
 * Seven rolling days rather than the calendar week the chart is bucketed on. The calendar bucket is
 * a few hours old on a Monday, so participation read as almost nobody at the start of every week and
 * climbed through it. The label says the last seven days and that is exactly what this counts.
 */

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Which of the participation figures a reader pressed, and so which list opens. */
export type ParticipationList = 'participating' | 'signal' | 'quiet';

export interface IFamilyParticipation {
    /** Everyone on the book. */
    clientCount: number;
    /** Clients whose family has put something on the record in the last seven days. */
    participatingCount: number;
    /** Whole percent, for the headline. */
    participationPercent: number;
    /** Observations recorded across the caseload in the last seven days. */
    signalCount: number;
    /** Clients with nothing this week, which is the list an agency works next. */
    quietCount: number;
    /** Who is contributing, for the drill-down. */
    participating: IOutcomeClient[];
    /** Who is not, for the drill-down. Same shape, so one modal serves both. */
    quiet: IOutcomeClient[];
}

const plural = (count: number, one: string, many: string): string => (count === 1 ? one : many);

export const buildFamilyParticipation = (digest: IAgencyDigest): IFamilyParticipation => {
    const now = new Date(digest.readAt).getTime();
    const from = now - WEEK_MS;

    const thisWeek = digest.windowFindings.filter(finding => {
        const at = new Date(finding.observedOn).getTime();
        return at >= from && at <= now;
    });

    const byClient = new Map<string, number>();
    for (const finding of thisWeek) {
        byClient.set(finding.careMapId, (byClient.get(finding.careMapId) ?? 0) + 1);
    }

    const participating: IOutcomeClient[] = [];
    const quiet: IOutcomeClient[] = [];

    for (const client of digest.clients) {
        const count = byClient.get(client.careMapId) ?? 0;
        const row: IOutcomeClient = {
            careMapId: client.careMapId,
            clientName: client.clientName,
            detail: count > 0
                ? `${count} ${plural(count, 'observation', 'observations')} on file this week.`
                : 'Nothing on file from the family this week.',
            drivers: []
        };
        if (count > 0) {
            participating.push(row);
        } else {
            quiet.push(row);
        }
    }

    const clientCount = digest.clients.length;
    // Every finding in the digest belongs to a client still on the book, so this is simply how many
    // landed in the window: the per-client tallies above can only ever add back up to it.
    const signalCount = thisWeek.length;

    return {
        clientCount,
        participatingCount: participating.length,
        participationPercent: clientCount === 0 ? 0 : Math.round((participating.length / clientCount) * 100),
        signalCount,
        quietCount: quiet.length,
        participating,
        quiet
    };
};

/** Which way the caseload's attention load has moved across the window. */
export type LoadDirection = 'rising' | 'steady' | 'easing';

export const loadDirection = (digest: IAgencyDigest): LoadDirection => {
    const change = digest.attentionTrend.recentPerWeek - digest.attentionTrend.earlierPerWeek;
    if (change > 0.1) {
        return 'rising';
    }
    if (change < -0.1) {
        return 'easing';
    }
    return 'steady';
};
